<script setup lang="ts">
import {
    DisabledPropsInterface,
    useDisableComposable,
} from '@src/application/composables/disabled-composable';
import { useRouterComposable } from '@router/router-composable';
import { computed, inject, onBeforeUnmount, Ref, watch } from 'vue';

const { label, tabName } = defineProps<DisabledPropsInterface & {
    tabName: string,
    label?: string|null,
}>();

const activeTab = inject<Ref<string|null>>('activeTab');

const context = inject<string|null>('context');

const contextClass = computed<string>(() => {
    if (!context) {
        return 'default';
    }
    return context;
});

const isActive = computed<boolean>(() => activeTab?.value === tabName);

const emits = defineEmits<((event: 'is-active', active: boolean) => void)>();

watch(isActive, () => emits('is-active', isActive.value), {
    immediate: true,
});

const ignoreRouteParam = inject<Ref<boolean>>('ignoreRouteParam');

const { disabled } = useDisableComposable();

onBeforeUnmount(() => {
    activeTab!.value = null;
});

const { pushParams } = useRouterComposable();

function switchTab() {
    if (disabled.value === true) {
        return;
    }

    if (!ignoreRouteParam?.value) {
        pushParams({ tab: tabName });
    }
    activeTab!.value = tabName;
}

const headerRef = inject<Ref<string|null>>('headerRef');
</script>

<template>
    <div>
        <Teleport v-if="headerRef" :to="headerRef">
            <div :class="[$style[contextClass], $style.base_tab_label, activeTab === tabName ? $style.active : '', disabled ? $style.disabled : '']" @click="switchTab">
                <p v-if="label">{{ label }}</p>
                <slot name="tab-header"/>
            </div>
        </Teleport>
        <div v-show="isActive">
            <slot/>
        </div>
    </div>
</template>

<style lang="scss" module>
@use '@text' as *;
@use '@borders' as *;
@use '@backgrounds' as *;

$disabled-tab-text: #8c8c8c;
$disabled-tab-background: #e6e6e6;

.base_tab_label {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: $thin-line-grey;

    &.disabled {
        color: $disabled-tab-text;
        cursor: not-allowed;
        background-color: $disabled-tab-background;
        border-color: $disabled-tab-background;
    }
    > * + * { margin-left: .7em }
    * { cursor: inherit }
}

.default.base_tab_label {
    min-width: 5em;
    height: 2.6em;
    padding-right: 1em;
    padding-left: 1em;
    margin-right: .3rem;
    border-bottom: none;

    @media (min-width: 33em) {
        margin-right: .5rem;
    }

    &.active:not(.disabled) {
        position: relative;
        z-index: 0;
        color: $primary-text-color;

        &::before, &::after {
            position: absolute;
            left: 0;
            z-index: 2;
            display: block;
            width: 100%;
            content: '';
        }

        &::before {
            top: 0;
            height: 3px;
            background-color: $primary-background;
            box-shadow: 0 0 0 1px $primary-background;
        }

        &::after {
            bottom: -2px;
            height: 5px;
            background-color: #fff;
        }
    }
}
</style>
